import React, { useState } from 'react'
import { Button } from '~/lib/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import Dropzone from './Dropzone'
import { useToast } from '~/lib/ui/toast'
import { apiPost } from '~/util/api'
import { useResume } from '../ResumeProvider'
import { useTranslation } from '~/hooks/useTranslation'

const ImportDialog = ({ children }) => {
  const { t } = useTranslation('ImportDialog')
  const [open, setOpen] = useState(false)
  const [acceptedFile, setAcceptedFile] = useState(null)
  const [showSaveOptions, setShowSaveOptions] = useState(false)
  const [resumeIsUploading, setResumeIsUploading] = useState(false)
  const { toast } = useToast()
  const { persistResume, documentOrSettingsChanged } = useResume()

  const handleImportClick = () => {
    if (documentOrSettingsChanged) {
      setShowSaveOptions(true)
    } else {
      importResume()
    }
  }

  const handleSaveExistingClick = () => {
    persistResume(false)
    importResume()
  }

  const handleDiscardChangesClick = () => {
    window.onbeforeunload = undefined
    importResume()
  }

  const closeDialog = () => {
    setOpen(false)
    setAcceptedFile(null)
    setShowSaveOptions(false)
    setResumeIsUploading(false)
  }

  const onOpenChange = (isOpen) => {
    isOpen ? setOpen(true) : closeDialog()
    return isOpen
  }

  const importResume = async () => {
    setResumeIsUploading(true)
    const name = acceptedFile.name.split('.')[0]
    const lastModified = acceptedFile.lastModifiedDate.toISOString()
    const formData = new FormData()
    formData.append('imported_resume', acceptedFile)
    formData.append('revision_date', lastModified)
    formData.append('name', name)
    formData.append('parse_resume', true)
    try {
      const response = await apiPost('/resume/builder', formData, true)
      setResumeIsUploading(false)
      toast({ description: 'Resume uploaded successfully' })
      window.location.href = `/resume/builder/${response.data.document_id}`
    } catch (e) {
      setResumeIsUploading(false)
      console.error('Error saving new resume', e)
      toast({
        description: 'Error: Resume upload failed.',
        variant: 'destructive',
      })
      return
    }
  }

  return (
    <Dialog open={open} className="rounded-2xl" {...{ open, onOpenChange }}>
      {/* CTS-9247 — focus-visible: link variant suppresses ring; add outline as focus indicator */}
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          className="whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          variant="link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-2xl xl:max-w-3xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">{t('import_resume')}</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          <p>{t('import_existing_resume_description')}</p>

          <p>{t('different_resume_design_description')}</p>

          <p>{t('get_started_text')}</p>
          <Dropzone {...{ acceptedFile, setAcceptedFile }} />
          <>
            {showSaveOptions ? (
              <div className="mt-4 rounded-lg bg-yellow-50 p-4">
                <p className="text-lg font-medium">
                  {t('unsaved_changes_warning')}
                </p>
                <div className="mt-4 flex flex-row gap-x-2">
                  <Button
                    disabled={resumeIsUploading}
                    onClick={handleSaveExistingClick}
                  >
                    {t('save_changes')}
                  </Button>
                  <Button
                    disabled={resumeIsUploading}
                    onClick={handleDiscardChangesClick}
                  >
                    {t('discard_changes')}
                  </Button>
                  <Button
                    disabled={resumeIsUploading}
                    onClick={closeDialog}
                    variant="outline"
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-row gap-x-2">
                <Button
                  disabled={!acceptedFile || resumeIsUploading}
                  onClick={handleImportClick}
                >
                  {t('import_my_resume')}
                </Button>
                <Button variant="outline" onClick={closeDialog}>
                  {t('cancel')}
                </Button>
              </div>
            )}
          </>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default ImportDialog
